import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Divider,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle,
  Star,
  Business,
  Security,
  TrendingUp as UpgradeIcon,
  TrendingDown as DowngradeIcon,
  SwapHoriz as SwitchIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  SubscriptionPlan,
  getCorporatePlans,
  getLawEnforcementPlans,
  formatPrice,
  getAnnualSavings,
  getPlanById,
} from '../../../constants/Subscriptions';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '90vh',
  },
}));

const PlanCard = styled(Card)<{ selected?: boolean; current?: boolean }>(
  ({ theme, selected, current }) => ({
    marginBottom: '15px',
    borderRadius: '16px',
    cursor: 'pointer',
    border: current
      ? `3px solid ${theme.palette.success.main}`
      : selected
      ? `3px solid ${theme.palette.primary.main}`
      : `2px solid ${theme.palette.divider}`,
    backgroundColor: current
      ? theme.palette.success.light + '10'
      : theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      border: current
        ? `3px solid ${theme.palette.success.main}`
        : `3px solid ${theme.palette.primary.light}`,
    },
  })
);

function isEnterprisePlan(plan: SubscriptionPlan): boolean {
  return (
    plan.id === 'Corp-Enterprise' ||
    plan.id === 'LEO-Enterprise' ||
    (plan.price === 0 && plan.id.toLowerCase().includes('enterprise'))
  );
}

interface PlanSwitchModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan?: SubscriptionPlan;
  onPlanSwitch?: (
    plan: SubscriptionPlan,
    isAnnual: boolean,
    options?: { enterpriseCode?: string }
  ) => Promise<void>;
}

const PlanSwitchModal: React.FC<PlanSwitchModalProps> = ({
  open,
  onClose,
  currentPlan,
  onPlanSwitch,
}) => {
  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'corporate' | 'law-enforcement'>('corporate');
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enterpriseCode, setEnterpriseCode] = useState('');

  const currentPlans = selectedCategory === 'corporate' ? getCorporatePlans() : getLawEnforcementPlans();
  
  useEffect(() => {
    if (currentPlan) {
      setSelectedCategory(currentPlan.category);
    }
  }, [currentPlan]);
  
  // Don't render if we don't have valid current plan data when currentPlan is provided
  if (currentPlan && (!currentPlan.id || typeof currentPlan.id !== 'string')) {
    return null;
  }

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    if (plan.id === currentPlan?.id) return; // Can't select current plan
    setSelectedPlan(plan);
    setEnterpriseCode('');
    setError('');
  };

  const handleCategoryChange = (newCategory: 'corporate' | 'law-enforcement') => {
    setSelectedCategory(newCategory);
    setSelectedPlan(null);
    setError('');
  };

  const getChangeType = (newPlan: SubscriptionPlan): 'upgrade' | 'downgrade' | 'switch' => {
    if (!currentPlan) return 'upgrade';
    
    // Compare by price first
    const currentPrice = currentPlan.price;
    const newPrice = newPlan.price;
    
    if (newPrice > currentPrice) return 'upgrade';
    if (newPrice < currentPrice) return 'downgrade';
    
    // If same price, it's a lateral switch
    return 'switch';
  };

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'upgrade': return <UpgradeIcon color="success" />;
      case 'downgrade': return <DowngradeIcon color="warning" />;
      default: return <SwitchIcon color="info" />;
    }
  };

  const getChangeColor = (changeType: string) => {
    switch (changeType) {
      case 'upgrade': return 'success';
      case 'downgrade': return 'warning';
      default: return 'info';
    }
  };

  const handleConfirmSwitch = async () => {
    if (!selectedPlan) return;

    if (isEnterprisePlan(selectedPlan) && !enterpriseCode.trim()) {
      setError('Enter your enterprise activation code to switch to this plan.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (onPlanSwitch) {
        await onPlanSwitch(selectedPlan, isAnnual, {
          enterpriseCode: isEnterprisePlan(selectedPlan) ? enterpriseCode.trim() : undefined,
        });
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to switch plan');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedPlan(null);
      setEnterpriseCode('');
      setError('');
      onClose();
    }
  };

  const paperSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  };
  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" PaperProps={{ sx: paperSx }}>
      <DialogTitle
        sx={{
          fontFamily: "'Inter', sans-serif",
          color: '#ffffff',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
          backgroundColor: 'transparent',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'none', letterSpacing: 'normal', fontSize: '1.25rem' }}>
              Switch Your Plan
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Choose a new plan that better fits your needs
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {/* Current Plan Display */}
        {currentPlan && currentPlan.id && (
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Current Plan
            </Typography>
            <Card
              sx={{
                borderRadius: '16px',
                border: `2px solid ${colors.Success || '#4caf50'}`,
                backgroundColor: `${colors.Success || '#4caf50'}10`,
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {currentPlan.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentPlan.description}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {formatPrice(currentPlan)}
                    </Typography>
                    <Chip label="Current" color="success" size="small" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Category Selection */}
        <Tabs
          value={selectedCategory}
          onChange={(_, newValue) => handleCategoryChange(newValue)}
          centered
          sx={{ marginBottom: 3 }}
        >
          <Tab
            value="corporate"
            label="Corporate"
            icon={<Business />}
            iconPosition="start"
            sx={{ minWidth: 150 }}
          />
          <Tab
            value="law-enforcement"
            label="Law Enforcement"
            icon={<Security />}
            iconPosition="start"
            sx={{ minWidth: 150 }}
          />
        </Tabs>

        {/* Annual Pricing Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                Annual Billing {isAnnual && '(Save up to 2 months!)'}
              </Typography>
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '16px' }}>
            {error}
          </Alert>
        )}

        {/* Plan Selection */}
        <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
          {currentPlans.map((plan) => {
            const savings = getAnnualSavings(plan);
            const isCurrentPlan = plan.id === currentPlan?.id;
            const isSelected = selectedPlan?.id === plan.id;
            const changeType = getChangeType(plan);

            return (
              <PlanCard
                key={plan.id}
                selected={isSelected}
                current={isCurrentPlan}
                onClick={() => !isCurrentPlan && handlePlanSelect(plan)}
                elevation={isSelected ? 8 : 2}
                sx={{
                  cursor: isCurrentPlan ? 'default' : 'pointer',
                  opacity: isCurrentPlan ? 0.8 : 1,
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight="bold">
                          {plan.title}
                        </Typography>
                        {plan.isPopular && (
                          <Chip
                            label="Popular"
                            size="small"
                            icon={<Star />}
                            color="warning"
                          />
                        )}
                        {isCurrentPlan && (
                          <Chip label="Current" size="small" color="success" />
                        )}
                        {isSelected && !isCurrentPlan && (
                          <Chip
                            label={changeType.charAt(0).toUpperCase() + changeType.slice(1)}
                            size="small"
                            color={getChangeColor(changeType) as any}
                            icon={getChangeIcon(changeType)}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {plan.description}
                      </Typography>

                      {/* Plan Details */}
                      <Box sx={{ mt: 1 }}>
                        {(plan.maxSites ?? 0) > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Sites: {plan.maxSites}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          Users: {plan.maxUsers ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          History: {plan.historyRetention ?? 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {formatPrice(plan, isAnnual)}
                      </Typography>
                      {isAnnual && savings > 0 && (
                        <Chip
                          label={`Save $${savings}`}
                          size="small"
                          color="success"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Features List - Condensed */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Included with this plan:
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 0.5,
                      }}
                    >
                      {plan.features.slice(0, 4).map((feature, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={0.5}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="caption" color="text.primary">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                      {plan.features.length > 4 && (
                        <Typography variant="caption" color="text.secondary">
                          +{plan.features.length - 4} more features
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </PlanCard>
            );
          })}
        </Box>

        {selectedPlan && isEnterprisePlan(selectedPlan) && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Enterprise activation code"
              placeholder="Enter your enterprise activation code"
              value={enterpriseCode}
              onChange={(e) => setEnterpriseCode(e.target.value)}
              disabled={loading}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              Enterprise is activated with a code from SkimBuster. No payment step is required in the browser.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleConfirmSwitch}
          disabled={!selectedPlan || loading}
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
          }}
        >
          {loading ? 'Switching...' : `Switch to ${selectedPlan?.title || 'Selected Plan'}`}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default PlanSwitchModal;